import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Input, PasswordInput, Button, Segmented } from '../components/ui';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { setCredentials, type Flow } from '../features/auth/authSlice';
import { useLoginMutation } from '../features/auth/authApi';
import { LogoShieldIcon } from '../assets/icons';

/** Turn an RTK Query error into a human-readable message. */
function loginErrorMessage(err: unknown): string {
  const e = err as { status?: number | string; data?: { detail?: string } };
  if (e?.data?.detail) return e.data.detail;
  if (e?.status === 'FETCH_ERROR') return 'Network error — please check your connection and try again.';
  return 'Sign in failed. Please try again.';
}

const LoginSchema = Yup.object({
  flow: Yup.mixed<Flow>().oneOf(['admin', 'user']).required(),
  email: Yup.string().email('Enter a valid email address').required('Email is required'),
  password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
});

interface LoginValues {
  flow: Flow;
  email: string;
  password: string;
}

export default function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.auth.user);
  const [login, { isLoading }] = useLoginMutation();

  const formik = useFormik<LoginValues>({
    initialValues: { flow: 'admin', email: '', password: '' },
    validationSchema: LoginSchema,
    onSubmit: async (values, helpers) => {
      helpers.setStatus(undefined);
      try {
        const res = await login({ email: values.email, password: values.password }).unwrap();
        const isAdmin = values.flow === 'admin';
        dispatch(
          setCredentials({
            token: res.access,
            user: {
              id: res.user.id,
              email: res.user.email,
              name: res.user.full_name || res.user.email,
              flow: values.flow,
              role: isAdmin ? 'System Administrator' : 'MLA / Field Representative',
            },
          }),
        );
        navigate(isAdmin ? '/admin' : '/user', { replace: true });
      } catch (err) {
        helpers.setStatus(loginErrorMessage(err));
      }
    },
  });

  // Already signed in — skip the login screen.
  if (user) {
    return <Navigate to={user.flow === 'admin' ? '/admin' : '/user'} replace />;
  }

  const { values, errors, touched, status, handleChange, handleBlur, handleSubmit, setFieldValue } =
    formik;

  return (
    <div className="flex min-h-screen bg-brand-bg">
      {/* Brand hero panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-brand-dark p-12 text-white lg:flex">
        <div className="absolute -right-24 -top-24 size-72 rounded-full bg-white/10" />
        <div className="absolute -bottom-32 -left-16 size-80 rounded-full bg-white/5" />
        <span className="text-2xl font-bold tracking-wide">CHUNAAV</span>
        <div className="relative z-10 flex flex-col gap-4">
          <h2 className="text-4xl font-semibold leading-tight">
            Manage your constituency, <br /> A&nbsp;to&nbsp;Z.
          </h2>
          <p className="max-w-md text-sm text-white/70">
            Comprehensive governance for MLAs, MPs and system administrators — role-aware
            navigation, creation loops and field feedback in one place.
          </p>
        </div>
        <span className="relative z-10 text-xs text-white/50">© 2026 Chunaav · Oogwai</span>
      </div>

      {/* Form panel */}
      <div className="flex w-full items-center justify-center p-6 lg:w-1/2">
        <div className="w-full max-w-[420px]">
          <div className="mb-8 flex flex-col items-center gap-3 text-center">
            <span className="flex size-12 items-center justify-center rounded-xl bg-brand text-white">
              <LogoShieldIcon className="size-6" />
            </span>
            <h1 className="text-2xl font-semibold tracking-[-0.5px] text-ink">Welcome back</h1>
            <p className="text-sm text-ink-secondary">Sign in to continue to your dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
            {/* Flow selection */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-ink">Which flow do you want to see?</label>
              <Segmented<Flow>
                block
                value={values.flow}
                onChange={(val) => setFieldValue('flow', val)}
                options={[
                  { label: 'Administrative Flow', value: 'admin' },
                  { label: 'User Flow', value: 'user' },
                ]}
              />
            </div>

            {/* Email */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className="text-sm font-medium text-ink">
                Email address
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                status={touched.email && errors.email ? 'error' : ''}
              />
              {touched.email && errors.email && (
                <span className="text-xs text-red-500">{errors.email}</span>
              )}
            </div>

            {/* Password */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="password" className="text-sm font-medium text-ink">
                Password
              </label>
              <PasswordInput
                id="password"
                name="password"
                placeholder="••••••••"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                status={touched.password && errors.password ? 'error' : ''}
              />
              {touched.password && errors.password && (
                <span className="text-xs text-red-500">{errors.password}</span>
              )}
            </div>

            {status && (
              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-center text-sm font-medium text-[#ba1a1a]">
                {status}
              </p>
            )}

            <Button
              variant="primary"
              type="submit"
              size="lg"
              block
              className="mt-2"
              disabled={isLoading}
            >
              {isLoading ? 'Signing in…' : 'Sign in'}
            </Button>

            <p className="text-center text-xs text-ink-secondary">
              Sign in with your Chunaav account credentials.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
